import { NavigatorScreenParams, useNavigation, useNavigationState } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { APITypes, Dynamo, MedicalDataTypes } from '../primary-record-types';
import { NoteDestinationParams } from '../components/shared/Notes/utils/types';
import { useCallback, useMemo } from 'react';
import React from 'react';
import useMedicalProfile from '../hooks/api/useMedicalProfile';
import useMedicalProfiles from '../hooks/api/useMedicalProfiles';
import { ReportType } from '../primary-record-types/reports';
import { AppType, useMaybeLoadingAppType } from '../hooks/useAppType';
import { useSetUrlUserType } from '../hooks/useUrlAppParams';

type CardType = 'insurance' | 'id';

export type NoteEditTab = 'note' | 'attachment';

export type ProfileTab = 'dashboard' | 'health' | 'manage';

export type EditProfileTab = 'information' | 'idCards' | 'advancedDirectives';

export type OrganizationTab =
    | 'organization'
    | 'organization-users'
    | 'billing'
    | 'groups'
    | 'invitation-definitions'
    | 'subscription'
    | 'client-billing';

// root navigation routes cannot have params
export type RootAppNavigationParamList = {
    welcome: undefined;
    login: { appType: AppType } | undefined;
    signup: { appType: AppType } | undefined;
    app: NavigatorScreenParams<TabNavigationParamList>;
    'shared-report': { shareCode: string };
    redirect: undefined;
};
export const useRootNavigation = () => useNavigation<NativeStackNavigationProp<RootAppNavigationParamList>>();

// tab navigation routes cannot have params
export type TabNavigationParamList = {
    activity: NavigatorScreenParams<ActivityTabNavigationParamList>;
    profiles: NavigatorScreenParams<ProfilesTabParamList>;
    clients: NavigatorScreenParams<ProfilesTabParamList>;
    notes: NavigatorScreenParams<NotesTabNavigationParamList>;
    account: NavigatorScreenParams<AccountTabParamList>;
    resources: NavigatorScreenParams<ResourcesTabParamList>;
    organization: NavigatorScreenParams<OrganizationTabParamList>;
};
export const useTabNavigation = () => useNavigation<NativeStackNavigationProp<TabNavigationParamList>>();

// activity tab
export type ActivityTabNavigationParamList = {
    root: undefined;
    details: {
        profileId: string;
        auditLogId: string;
    };
    'add-message': {
        profileIds: string[];
    };
};
export const useActivityTabNavigation = () =>
    useNavigation<NativeStackNavigationProp<ActivityTabNavigationParamList>>();

// notes tab
export type NotesTabNavigationParamList = {
    root: undefined;
    add: NoteDestinationParams;
    details: { noteId: string };
    edit: { noteId: string; defaultTab?: NoteEditTab };
    transfer: { noteId: string };
    send: { noteId: string };
};
export const useNotesTabNavigation = () => useNavigation<NativeStackNavigationProp<NotesTabNavigationParamList>>();
export const useNestedNavigation = () => {
    const rootNavigation = useRootNavigation();
    const appType = useMaybeLoadingAppType();

    return useMemo(() => {
        const nestedNavigation = (tab: keyof TabNavigationParamList, screen: string, value: any) => {
            rootNavigation.navigate('app', {
                screen: tab,
                initial: false,
                params: {
                    screen: screen as any,
                    initial: false,
                    params: value,
                },
            });
        };

        return {
            nestedNotesNavigate: <K extends keyof NotesTabNavigationParamList>(
                screen: K,
                value: NotesTabNavigationParamList[K],
            ) => {
                nestedNavigation('notes', screen, value);
            },
            nestedActivityNavigate: <K extends keyof ActivityTabNavigationParamList>(
                screen: K,
                value: ActivityTabNavigationParamList[K],
            ) => {
                nestedNavigation('activity', screen, value);
            },
            nestedProfileNavigation: <K extends keyof ProfilesTabParamList>(
                screen: K,
                value: ProfilesTabParamList[K],
            ) => {
                nestedNavigation(appType === 'consumer' ? 'profiles' : 'clients', screen, value);
            },
            nestedAccountNavigation: <K extends keyof AccountTabParamList>(
                screen: K,
                value: AccountTabParamList[K],
            ) => {
                nestedNavigation('account', screen, value);
            },
            nestedResourcesNavigation: <K extends keyof ResourcesTabParamList>(
                screen: K,
                value: ResourcesTabParamList[K],
            ) => {
                nestedNavigation('resources', screen, value);
            },
            nestedOrganizationNavigation: <K extends keyof OrganizationTabParamList>(
                screen: K,
                value: OrganizationTabParamList[K],
            ) => {
                nestedNavigation('organization', screen, value);
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootNavigation]);
};

export const useTabDefaultScreenNavigation = () => {
    const tabNavigation = useTabNavigation();
    const nestedNavigation = useNestedNavigation();

    return useCallback(
        (tab: keyof TabNavigationParamList) => {
            if (tab === 'account') {
                nestedNavigation.nestedAccountNavigation('root', undefined);
            } else if (tab === 'activity') {
                nestedNavigation.nestedActivityNavigate('root', undefined);
            } else if (tab === 'notes') {
                nestedNavigation.nestedNotesNavigate('root', undefined);
            } else if (tab === 'profiles' || tab === 'clients') {
                nestedNavigation.nestedProfileNavigation('root', undefined);
            } else if (tab === 'resources') {
                nestedNavigation.nestedResourcesNavigation('root', undefined);
            } else if (tab === 'organization') {
                nestedNavigation.nestedOrganizationNavigation('root', {});
            } else {
                console.error('No default screen for tab');
                tabNavigation.navigate(tab);
            }
        },
        [nestedNavigation, tabNavigation],
    );
};

const ActiveTabContext = React.createContext<keyof TabNavigationParamList | null>(null);
type NonProfileOrClientsTabNames = Exclude<keyof TabNavigationParamList, 'profiles' | 'clients'>;
/**
 * A wrapper to explicitly set the active tab context for a component.  Each root tab screen should use this HOC
 *
 * Using this instead of digging through the internal react-navigation state
 */
export const withActiveTab = <P extends object>(
    Component: React.ComponentType<P>,
    tab: NonProfileOrClientsTabNames | 'clients-or-profiles',
) => {
    return (props: P) => {
        const appType = useMaybeLoadingAppType();
        const mappedTab =
            tab === 'clients-or-profiles'
                ? appType === 'consumer'
                    ? ('profiles' as const)
                    : ('clients' as const)
                : tab;
        return (
            <ActiveTabContext.Provider value={mappedTab}>
                <Component {...props} />
            </ActiveTabContext.Provider>
        );
    };
};

const getLatestHistoryItem = (history: any[] | undefined): any => {
    return history?.[history.length - 1];
};

const getMostRecentLeafRoute = (state: any): any => {
    if (!state) {
        return null;
    }

    const route = getLatestHistoryItem(state.routes);
    if (route?.state) {
        return getMostRecentLeafRoute(route.state);
    }

    return route;
};

const useCurrentTabRoute = () => {
    const tabStackNavigation = useTabNavigation()?.getState();

    return getLatestHistoryItem(tabStackNavigation?.routes);
};

export const useActiveTab = (): { activeTab: keyof TabNavigationParamList | null; isRoot: boolean } => {
    const tab = React.useContext(ActiveTabContext);
    const currentRoute = useCurrentTabRoute();

    return {
        activeTab: tab,
        isRoot: currentRoute?.name === 'root',
    };
};

export type UseTabRouteActiveProfileReturnValue = {
    profileId: string | null;
    profile: APITypes.medicalProfileService.GetProfileResponse | null;
};

export const useTabRouteActiveProfile = (): UseTabRouteActiveProfileReturnValue => {
    const { collaboratorMedicalProfiles, medicalProfiles } = useMedicalProfiles();
    const accessibleProfileIds = [...collaboratorMedicalProfiles, ...medicalProfiles].map((_) => _.medicalProfileId);
    const activeTab = useActiveTab();
    const state = useNavigationState((_) => _);
    const routeName = getMostRecentLeafRoute(state).name;
    const profileId = getMostRecentLeafRoute(state)?.params?.profileId || null;
    const isInviteWithoutProfileAccess =
        !!profileId && !accessibleProfileIds.includes(profileId) && routeName === 'invite';
    const { isLoadingProfile, profile } = useMedicalProfile({
        medicalProfileId: isInviteWithoutProfileAccess ? null : profileId,
    });
    if (activeTab.activeTab !== 'profiles' || !profileId) {
        return { profileId: null, profile: null };
    }

    return { profileId, profile: isLoadingProfile ? null : profile || null };
};

export type ActiveTabAndActiveScreen = {
    activeTab: keyof TabNavigationParamList;
    activeScreen:
        | keyof ActivityTabNavigationParamList
        | keyof ProfilesTabParamList
        | keyof NotesTabNavigationParamList
        | keyof AccountTabParamList
        | keyof ResourcesTabParamList;
    params: Record<string, string>;
};

export const useActiveTabActiveScreen = (): ActiveTabAndActiveScreen | null => {
    const activeTab = useActiveTab();
    const state = useNavigationState((_) => _);
    const activeScreen = getMostRecentLeafRoute(state)?.name;
    return activeTab?.activeTab && activeScreen
        ? {
              activeTab: activeTab.activeTab as any,
              activeScreen: activeScreen as any,
              params: getMostRecentLeafRoute(state)?.params || {},
          }
        : null;
};

/**
 * A wrapper for react-navigation's goBack functionality that handles edge cases around
 * not having a screen to go back to
 */
export const useGoBack = () => {
    const { activeTab } = useActiveTab();
    const tabNavigation = useTabNavigation();
    const navigateToTabDefaultScreen = useTabDefaultScreenNavigation();
    const { nestedActivityNavigate } = useNestedNavigation();

    return useCallback(() => {
        if (tabNavigation?.goBack && tabNavigation.canGoBack()) {
            // first default to standard goBack if available
            tabNavigation.goBack();
        } else if (activeTab) {
            // if we can't go back, kick the user out to the root screen of the tab if available
            navigateToTabDefaultScreen(activeTab);
        } else {
            // no root screen found, just navigate to "home"
            nestedActivityNavigate('root', undefined);
        }
    }, [activeTab, navigateToTabDefaultScreen, nestedActivityNavigate, tabNavigation]);
};

type AuthPath = 'login' | 'signup';

const useNavigateToUserApp = (appType: AppType) => {
    const navigation = useRootNavigation();
    const setUrlUserType = useSetUrlUserType();

    const navigateToApp = async (path: AuthPath) => {
        await setUrlUserType(appType);
        navigation.navigate(path, { appType });
    };

    return navigateToApp;
};

export const useNavigateToProApp = () => useNavigateToUserApp('professional');

export const useNavigateToConsumerApp = () => useNavigateToUserApp('consumer');

export type FilterAllergyStatus = 'active' | 'inactive';

export type ClientRecordDetailsTab =
    | 'details'
    | 'invitation-details'
    | 'notes'
    | 'history'
    | 'organization-files'
    | 'activity';

export type ResourceSearchScreenParams = {
    searchText?: string;
    conditionStatus?: MedicalDataTypes.PrimaryRecordConditionStatus;
    allergyStatus?: FilterAllergyStatus;
    medicationStatus?: 'active' | 'inactive';
    visitStatus?: MedicalDataTypes.PrimaryRecordVisitType | 'other' | 'all';
    visitWindow?: 'next-month' | 'upcoming' | 'past' | 'all';
};

/**
 * booleans are stringified, so we need to use a type to represent them
 */
export type BooleanString = 'true' | 'false';

// profiles tab
export type ProfilesTabParamList = {
    root: undefined | { isRegistration?: BooleanString };
    add: { useUser: BooleanString };
    invite: { profileId: string; invitationId: string };
    profile: { defaultTab?: ProfileTab; profileId: string; firstName?: string; forcePortalRefresh?: BooleanString };
    edit: { profileId: string; firstName: string; defaultTab?: EditProfileTab };
    'contact-details': { profileId: string };
    'emergency-contacts': { profileId: string };
    'edit-emergency-contact': { profileId: string; emergencyContactId: string };
    'create-emergency-contact': { profileId: string };
    'care-team': { profileId: string };
    'add-care-team': { profileId: string; associatedResourceId?: string };
    'care-team-member': { profileId: string; careTeamMemberId: string };
    'edit-care-team-member': { profileId: string; careTeamMemberId: string };
    'copy-care-team-member': { profileId: string; careTeamMemberId: string };
    'add-related-care-team-member': { profileId: string; careTeamMemberId: string };
    'invite-collaborator': {
        profileId: string;
        careTeamMemberId?: string;
        defaultFirstName?: string;
        defaultLastName?: string;
        defaultEmail?: string;
    };
    'invite-details': { profileId: string; invitationId: string };
    collaborator: { profileId: string; collaboratorId: string };
    'collaborator-invoice': { profileId: string; collaboratorId: string; invoiceId: string };
    'add-transfer-request': { profileId: string; collaboratorId?: string };
    'transfer-request': { profileId: string };
    'transfer-request-response': { transferProfileId: string; transferRequestId: string };
    'add-card': { profileId: string; cardType: CardType };
    'edit-card': { profileId: string; cardType: CardType; cardId: string; description: string };
    data: { profileId: string; category: MedicalDataTypes.PrimaryRecordResourceCategory };
    'data-bulk-update': {
        profileId: string;
        category: MedicalDataTypes.PrimaryRecordResourceCategory;
    } & ResourceSearchScreenParams;
    'data-details': {
        profileId: string;
        category: MedicalDataTypes.PrimaryRecordResourceCategory;
        resourceId: string;
    };
    'add-data': {
        profileId: string;
        category: MedicalDataTypes.PrimaryRecordResourceCategory;
        sourceResourceId: string | null;
        careTeamMemberId?: string;
        startDateTime?: string;
    };
    'edit-data': {
        profileId: string;
        category: MedicalDataTypes.PrimaryRecordResourceCategory;
        resourceId: string;
    };
    'add-portal': {
        profileId: string;
    };
    'add-fasten-portal': {
        profileId: string;
    };
    'add-tefca-connection': {
        profileId: string;
    };
    'add-note': NoteDestinationParams;
    'copy-note': {
        profileId: string;
        noteId: string;
        noteEntityType: Dynamo.NoteEntityType;
    };
    note: { noteId: string; noteEntityType: Dynamo.NoteEntityType; profileId: string };
    'edit-note': { noteId: string; noteEntityType: Dynamo.NoteEntityType; defaultTab?: NoteEditTab; profileId: string };
    'send-note': { noteId: string; noteEntityType: Dynamo.NoteEntityType; profileId: string };
    'transfer-note': { noteId: string; noteEntityType: Dynamo.NoteEntityType; profileId: string };
    'portal-connection': { profileId: string; healthSystemId: string };
    'fasten-portal-connection': { profileId: string; orgConnectionId: string; autoReconnect?: string };
    reports: { profileId: string };
    'add-shared-report': { profileId: string; reportType: ReportType };
    'shared-report': { profileId: string; sharedReportId: string };
    files: { profileId: string; defaultFolderId?: string };
    file: {
        profileId: string;
        fileId: string;
        page?: string;
        bbTop?: string;
        bbLeft?: string;
        bbWidth?: string;
        bbHeight?: string;
        startTime?: string;
    };
    chat: {
        profileId: string;
        defaultChatId?: string;
        defaultPrompt?: string;
        disableSetDefaultChat?: BooleanString;
    };
    'date-labs': {
        profileId: string;
        date: string;
    };
    'lab-history': {
        profileId: string;
        labCode: string;
    };
    'profile-activity': {
        profileId: string;
    };
    'profile-activity-details': {
        auditLogId: string;
        profileId: string;
    };
    'message-profile': {
        profileId: string;
    };
    'client-profile': {
        profileId: string;
    };
    'edit-client-profile-groups': {
        clientRecordId: string;
    };
    'client-record': {
        clientRecordId: string;
        defaultTab?: ClientRecordDetailsTab;
    };
    'edit-client-record': {
        clientRecordId: string;
    };
    'add-client-record': undefined;
    'add-client-invitation': undefined;
    'add-client-record-invitation': {
        clientRecordId: string;
    };
    'edit-client-invitation': {
        clientRecordId: string;
    };
    'client-record-note': {
        clientRecordId: string;
        noteId: string;
    };
    'add-client-record-note': {
        clientRecordId: string;
    };
    'edit-client-record-note': {
        clientRecordId: string;
        noteId: string;
    };
    'edit-client-billing-info': {
        clientRecordId: string;
    };
    'copy-org-note-to-clients': {
        medicalProfileIds: string[];
    };
    'copy-visit-to-clients': {
        medicalProfileIds: string[];
    };
    'copy-org-care-team-member-to-clients': {
        medicalProfileIds: string[];
    };
    'send-clients-message': {
        clientMedicalProfileIds: string[];
    };
    'clients-activity': {
        clientMedicalProfileIds: string[];
    };
    'client-activity-details': {
        auditLogId: string;
        clientMedicalProfileId: string;
    };
    'org-invite-accept-success': {
        clientProfileId: string;
        organizationId: string;
    };
    'client-activity-history': {
        clientRecordId: string;
    };
    'client-invoice-items': {
        clientRecordId: string;
    };
    'message-preferences': {
        selectedClientRecordIds: string[];
    };
    connect: {
        profileId: string;
        forcePortalRefresh?: BooleanString;
        forceFastenRefresh?: BooleanString;
        showFastenPendingAlert?: BooleanString;
    };
    access: {
        profileId: string;
    };
    'profile-notes': {
        profileId: string;
    };
    messages: {
        profileId: string;
    };
    'bulk-add-client-records': {};
    'bulk-add-client-profiles-groups': {
        clientRecordIds: string[];
    };
    'add-suggested-profile-folders': {
        profileId: string;
    };
    'edit-code-status': {
        profileId: string;
    };
    'edit-legal-decision-maker': {
        profileId: string;
    };
    'bulk-invite-clients': {
        clientRecordsStorageKey: string;
    };
    'visit-invite-emails': {
        profileId: string;
    };
};
export const useProfilesTabNavigation = () => useNavigation<NativeStackNavigationProp<ProfilesTabParamList>>();

// account tab
export type AccountTabParamList = {
    root: undefined;
    information: { defaultToEdit?: BooleanString };
    support: undefined;
    subscription: undefined;
    'account-deletion-confirmation': undefined;
    preferences: undefined;
    redirect: undefined;
};
export const useAccountTabNavigation = () => useNavigation<NativeStackNavigationProp<AccountTabParamList>>();

export type ResourcesTabParamList = {
    root: undefined;
    note: { noteId: string };
    'edit-note': { noteId: string };
    'add-note': undefined;
    'add-care-team-member': undefined;
    'edit-care-team-member': { careTeamMemberId: string };
    'care-team-member': { careTeamMemberId: string };
};
export const useResourcesTabNavigation = () => useNavigation<NativeStackNavigationProp<ResourcesTabParamList>>();

export type OrganizationTabParamList = {
    root: { defaultTab?: OrganizationTab };
    'add-organization-invitation': undefined;
    'organization-invitation': { invitationId: string };
    user: { organizationUserId: string };
    'edit-user': { organizationUserId: string };
    'add-group': undefined;
    group: { groupId: string };
    'edit-group': { groupId: string };
    'add-invitation-template': undefined;
    'edit-invitation-template': { invitationTemplateId: string };
    'invitation-template': { invitationTemplateId: string };
    'edit-client-billing-branding': {};
};
export const useOrganizationTabNavigation = () => useNavigation<NativeStackNavigationProp<OrganizationTabParamList>>();

type ExtractNestedKeys<T> = {
    [K in keyof T]: T[K] extends Record<string, any> ? keyof T[K] : never;
}[keyof T];

/**
 * Any possible param name from any navigation route
 */
export type AllParamNames =
    | ExtractNestedKeys<ActivityTabNavigationParamList>
    | ExtractNestedKeys<NotesTabNavigationParamList>
    | ExtractNestedKeys<AccountTabParamList>
    | ExtractNestedKeys<ProfilesTabParamList>
    | ExtractNestedKeys<TabNavigationParamList>
    | ExtractNestedKeys<RootAppNavigationParamList>
    | ExtractNestedKeys<ResourcesTabParamList>
    | ExtractNestedKeys<OrganizationTabParamList>;

const appNavigationNames = {
    root: {
        welcome: 'welcome',
        faqs: 'faqs',
        signup: 'signup',
        login: 'login',
        termsOfService: 'terms-of-service',
        app: 'app',
        sharedReport: 'shared-report',
        redirect: 'redirect',
    },
    tabs: {
        activity: 'activity',
        profiles: 'profiles',
        notes: 'notes',
        account: 'account',
        resources: 'resources',
        organization: 'organization',
        clients: 'clients',
    },
    activity: {
        root: 'root',
        auditLogDetails: 'details',
        addMessage: 'add-message',
    },
    notes: {
        root: 'root',
        add: 'add',
        details: 'details',
        edit: 'edit',
        transfer: 'transfer',
        send: 'send',
    },
    account: {
        root: 'root',
        information: 'information',
        support: 'support',
        subscription: 'subscription',
        accountDeletionConfirmation: 'account-deletion-confirmation',
        preferences: 'preferences',
        redirect: 'redirect',
    },
    profiles: {
        root: 'root',
        add: 'add',
        invite: 'invite',
        profile: 'profile',
        edit: 'edit',
        contactDetails: 'contact-details',
        emergencyContacts: 'emergency-contacts',
        editEmergencyContact: 'edit-emergency-contact',
        createEmergencyContact: 'create-emergency-contact',
        careTeam: 'care-team',
        addCareTeam: 'add-care-team',
        careTeamMember: 'care-team-member',
        editCareTeamMember: 'edit-care-team-member',
        copyCareTeamMember: 'copy-care-team-member',
        addRelatedCareTeamMember: 'add-related-care-team-member',
        inviteCollaborator: 'invite-collaborator',
        inviteDetails: 'invite-details',
        collaborator: 'collaborator',
        collaboratorInvoice: 'collaborator-invoice',
        addTransferRequest: 'add-transfer-request',
        transferRequest: 'transfer-request',
        transferRequestResponse: 'transfer-request-response',
        addCard: 'add-card',
        editCard: 'edit-card',
        data: 'data',
        dataBulkUpdate: 'data-bulk-update',
        dataDetails: 'data-details',
        addData: 'add-data',
        editData: 'edit-data',
        dataImage: 'data-image',
        addPortal: 'add-portal',
        addFastenPortal: 'add-fasten-portal',
        addTefcaConnection: 'add-tefca-connection',
        addNote: 'add-note',
        copyNote: 'copy-note',
        note: 'note',
        editNote: 'edit-note',
        sendNote: 'send-note',
        transferNote: 'transfer-note',
        portalConnection: 'portal-connection',
        fastenPortalConnection: 'fasten-portal-connection',
        reports: 'reports',
        addSharedReport: 'add-shared-report',
        sharedReport: 'shared-report',
        files: 'files',
        file: 'file',
        chat: 'chat',
        dateLabs: 'date-labs',
        labHistory: 'lab-history',
        profileActivity: 'profile-activity',
        profileActivityDetails: 'profile-activity-details',
        messageProfile: 'message-profile',
        clientProfile: 'client-profile',
        editClientProfileGroups: 'edit-client-profile-groups',
        bulkAddClientProfilesGroups: 'bulk-add-client-profiles-groups',
        clientRecord: 'client-record',
        addClientRecord: 'add-client-record',
        editClientRecord: 'edit-client-record',
        addClientInvitation: 'add-client-invitation',
        editClientInvitation: 'edit-client-invitation',
        addClientRecordInvitation: 'add-client-record-invitation',
        clientRecordNote: 'client-record-note',
        addClientRecordNote: 'add-client-record-note',
        editClientRecordNote: 'edit-client-record-note',
        copyOrgNoteToClients: 'copy-org-note-to-clients',
        copyVisitToClients: 'copy-visit-to-clients',
        copyOrgCareTeamMemberToClients: 'copy-org-care-team-member-to-clients',
        sendClientsMessage: 'send-clients-message',
        clientsActivity: 'clients-activity',
        clientActivityDetails: 'client-activity-details',
        orgInviteAcceptSuccess: 'org-invite-accept-success',
        clientActivityHistory: 'client-activity-history',
        connect: 'connect',
        access: 'access',
        profileNotes: 'profile-notes',
        bulkAddClientRecords: 'bulk-add-client-records',
        addSuggestedProfileFolders: 'add-suggested-profile-folders',
        editClientBillingInfo: 'edit-client-billing-info',
        clientInvoiceItems: 'client-invoice-items',
        messagePreferences: 'message-preferences',
        editCodeStatus: 'edit-code-status',
        editLegalDecisionMaker: 'edit-legal-decision-maker',
        bulkInviteClients: 'bulk-invite-clients',
        messages: 'messages',
        visitInviteEmails: 'visit-invite-emails',
    },
    resources: {
        root: 'root',
        note: 'note',
        editNote: 'edit-note',
        addNote: 'add-note',
        addCareTeamMember: 'add-care-team-member',
        editCareTeamMember: 'edit-care-team-member',
        careTeamMember: 'care-team-member',
    },
    organization: {
        root: 'root',
        addOrganizationInvitation: 'add-organization-invitation',
        organizationInvitation: 'organization-invitation',
        user: 'user',
        editUser: 'edit-user',
        addGroup: 'add-group',
        editGroup: 'edit-group',
        group: 'group',
        addInvitationDefinition: 'add-invitation-template',
        editInvitationDefinition: 'edit-invitation-template',
        invitationDefinition: 'invitation-template',
        editClientBillingBranding: 'edit-client-billing-branding',
    },
} as const;

export default appNavigationNames;
